import { state } from './state.js';
import * as UI from './ui.js';
import { UI_COPY } from './config/uiCopy.js';
import { SLIDERS, updateSliderDisplay } from './config/sliders.js';
import {
    toUiColorShare,
    toUiNoiseFactor,
    toUiReusePenalty,
    toUiTileHeight,
} from './config/setting_units.js';
import { getCsrfToken } from './api.js';
import { getOriginalSize } from './target_controller.js';
import { isAdminControlsEnabled } from './settings_mode.js';

const PRESET_STORAGE_KEY = 'mosaic_preset_id';

function isLegacyHighresDraftEnabled() {
    const toggle = document.getElementById('legacyHighresDraftToggle');
    return Boolean(toggle && toggle.checked);
}

function getStoredPresetId() {
    try {
        return String(localStorage.getItem(PRESET_STORAGE_KEY) || '').trim();
    } catch (e) {
        return '';
    }
}

function setStoredPresetId(id) {
    try {
        if (!id) {
            localStorage.removeItem(PRESET_STORAGE_KEY);
        } else {
            localStorage.setItem(PRESET_STORAGE_KEY, String(id));
        }
    } catch (e) {}
}

export async function init() {
    const sel = document.getElementById('presetSelect');
    const hint = document.getElementById('presetHint');
    if (!sel) return;
    if (hint) hint.textContent = UI_COPY.presets.defaultHint;

    try {
        const res = await fetch('/static/presets/index.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const index = await res.json();
        const presets = Array.isArray(index.presets) ? index.presets : [];
        sel.innerHTML = `<option value="">${UI_COPY.presets.chooseOption}</option>` +
            presets.map(p => `<option value="${p.id}">${p.name}</option>`).join('');
        sel.addEventListener('change', async () => {
            const id = sel.value;
            if (!id) {
                setStoredPresetId('');
                if (hint) hint.textContent = UI_COPY.presets.defaultHint;
                return;
            }
            setStoredPresetId(id);
            const p = presets.find(x => x.id === id);
            if (!p) return;
            if (hint) hint.textContent = p.description || UI_COPY.presets.fallbackLoaded;
            try {
                const r = await fetch(`/static/presets/${p.file}`, { cache: 'no-store' });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                applySettings(data.settings || data);
                logPresetUse(p);
            } catch (e) {
                console.warn('Failed to load preset', e);
            }
        });

        const stored = getStoredPresetId();
        const storedPreset = presets.find((p) => p.id === stored);
        if (storedPreset) {
            sel.value = stored;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
            setStoredPresetId('');
            sel.value = '';
            if (hint) hint.textContent = UI_COPY.presets.defaultHint;
        }
    } catch (e) {
        // Presets are optional. If files don't exist, silently ignore.
        console.warn('Presets unavailable', e);
        sel.style.display = 'none';
        const row = document.getElementById('presetRow');
        if (row) row.style.display = 'none';
    }
}

export function applySettings(raw) {
    const s = raw || {};
    const settingUnits = s.settings_units ?? s.settingsUnits ?? '';
    // Accept both manifest keys and friendly keys
    const buildMode = s.build_mode ?? s.buildMode;
    const enlargement = s.enlargement ?? s.scale ?? s.enlarge;
    const tileHeight = s.base_tile_height ?? s.tile_height ?? s.tileHeight;
    const colorMode = s.color_mode ?? s.coloring_style ?? s.colorStyle;
    const colorShare = s.color_share ?? s.color_match ?? s.colorMatch;
    const noise = s.noise_factor ?? s.random_noise ?? s.randomNoise;
    const reuse = s.reuse_penalty ?? s.reusePenalty;
    const allowFlips = s.allow_flips ?? s.allowFlips;
    const matcherBackend = s.matcher_backend ?? s.matcherBackend;
    const mosaicMode = s.mosaic_mode ?? s.mode ?? s.mosaicMode;
    const adaptiveSquareMinSize = (
        s.adaptive_square_min_size_px_requested
        ?? s.adaptive_square_min_size_px_resolved
        ?? s.adaptive_square_min_size_px
        ?? s.adaptiveSquareMinSizePx
    );
    const uiTileHeight = tileHeight != null ? toUiTileHeight(tileHeight) : null;
    const uiColorShare = colorShare != null ? toUiColorShare(colorShare, settingUnits) : null;
    const uiNoise = noise != null ? toUiNoiseFactor(noise, settingUnits) : null;
    const uiReuse = reuse != null ? toUiReusePenalty(reuse, settingUnits) : null;

    const setAndFire = (el, value, evt = 'input') => {
        if (!el || value === undefined || value === null) return;
        el.value = String(value);
        el.dispatchEvent(new Event(evt, { bubbles: true }));
    };

    const qualityToggle = document.getElementById('qualityToggle');
    if (qualityToggle && buildMode) {
        if (isAdminControlsEnabled()) {
            qualityToggle.checked = (String(buildMode).toLowerCase() === 'quality');
        } else {
            qualityToggle.checked = true;
        }
        qualityToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (isLegacyHighresDraftEnabled()) {
        setAndFire(document.getElementById('scaleInput'), enlargement);
    }
    setAndFire(document.getElementById('thInput'), uiTileHeight);
    setAndFire(document.getElementById('colorShare'), uiColorShare);
    setAndFire(document.getElementById('noiseFactor'), uiNoise);
    setAndFire(document.getElementById('rpInput'), uiReuse);
    const cm = document.getElementById('colorMode');
    if (cm && colorMode) {
        cm.value = String(colorMode);
        cm.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const af = document.getElementById('allowFlips');
    if (af && allowFlips !== undefined && allowFlips !== null) {
        af.checked = !!allowFlips;
        af.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const ms = document.getElementById('modeSelect');
    if (ms && mosaicMode) {
        ms.value = String(mosaicMode);
        ms.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const matcherBackendSelect = document.getElementById('matcherBackendSelect');
    if (matcherBackendSelect && matcherBackend) {
        matcherBackendSelect.value = String(matcherBackend);
        matcherBackendSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const adaptiveSquareMinSizeInput = document.getElementById('adaptiveSquareMinSizeInput');
    if (adaptiveSquareMinSizeInput && adaptiveSquareMinSize != null) {
        adaptiveSquareMinSizeInput.value = String(adaptiveSquareMinSize);
        adaptiveSquareMinSizeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Refresh label text (some labels are only updated via inline handlers)
    if (uiTileHeight !== undefined && uiTileHeight !== null) updateSliderDisplay('thInput', uiTileHeight);
    if (uiColorShare !== undefined && uiColorShare !== null) updateSliderDisplay('colorShare', uiColorShare);
    if (uiNoise !== undefined && uiNoise !== null) updateSliderDisplay('noiseFactor', uiNoise);
    if (uiReuse !== undefined && uiReuse !== null) updateSliderDisplay('rpInput', uiReuse);
    // Update tiles count/labels if we already have a target loaded
    const { origW, origH } = getOriginalSize();
    if (origW && origH) {
        UI.updateStats(origW, origH);
    }
}

function getDefaultSettings() {
    return {
        adaptive_square_min_size_px: 10,
        base_tile_height: SLIDERS.tileHeight.defaultValue,
        color_share: SLIDERS.colorShare.defaultValue,
        noise_factor: SLIDERS.noiseFactor.defaultValue,
        reuse_penalty: SLIDERS.reusePenalty.defaultValue,
        color_mode: 'smooth',
        allow_flips: true,
        build_mode: 'quality',
        mode: 'IRREGULAR',
    };
}

export function resetToDefaults() {
    setStoredPresetId('');
    const sel = document.getElementById('presetSelect');
    if (sel) sel.value = '';
    const hint = document.getElementById('presetHint');
    if (hint) hint.textContent = UI_COPY.presets.defaultHint;
    const simpleModeBtn = document.querySelector('#settingsMode .seg-btn[data-mode="simple"]');
    if (simpleModeBtn) simpleModeBtn.click();
    applySettings(getDefaultSettings());
}

async function logPresetUse(preset) {
    try {
        const headers = { 'Content-Type': 'application/json' };
        const csrfToken = getCsrfToken();
        if (csrfToken) headers['x-csrf-token'] = csrfToken;
        await fetch('/log-event', {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({
                event: 'preset_used',
                metadata: {
                    preset_id: preset.id,
                    preset_name: preset.name,
                    preset_file: preset.file
                }
            })
        });
    } catch (e) {
        console.warn('Preset log failed', e);
    }
}
